<template>
  <div class="events-list-wrapper">
    <div class="container">
      
      <!-- Header (Consistent with ProductDetail & ResellerList) -->
      <div class="nav-header animate-fade-in">
        <div class="nav-left">
          <v-btn icon @click="$router.push('/')" class="back-btn">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="nav-title">Torna</span>
        </div>
        
        <router-link to="/" class="nav-logo-link">
          <img 
            src="https://masticando-cdn.s3.eu-west-1.amazonaws.com/logo-435x435.png" 
            alt="Masticando" 
            class="header-logo"
          />
        </router-link>
      </div>

      <div class="content-animate-up">
        <h2 class="page-title">{{getTranslation('events-list')}}</h2>
        <div class="title-underline"></div>
        
        <!-- Events Grid -->
        <div class="events-grid" v-if="events.length > 0">
          <div 
            v-for="(ev, index) in events" 
            :key="ev.id" 
            class="event-card glass-card"
            :style="{ animationDelay: `${index * 0.1}s` }"
            @click="fullscreenImage = ev?.img?.formats?.large?.url || ev?.img?.url"
          >
            <div class="card-image-wrapper">
               <v-img 
                 :src="ev?.img?.formats?.medium?.url || ev?.img?.url" 
                 :alt="ev?.img?.name" 
                 cover
                 class="event-img"
               ></v-img>
               <div class="hover-overlay">
                 <v-icon color="white" size="large">mdi-magnify-plus-outline</v-icon>
               </div>
            </div>
            <!-- Date removed as requested -->
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state glass-card">
           <v-icon size="64" color="grey">mdi-calendar-remove</v-icon>
           <h3>Nessun evento in programma</h3>
           <p>Torna a visitarci per rimanere aggiornato sulle prossime date.</p>
        </div>

      </div>
    
      <!-- Fullscreen Image Dialog -->
      <v-dialog v-model="showFullScreenImage" transition="dialog-bottom-transition" fullscreen>
        <div class="fullscreen-wrapper" @click="fullscreenImage = null">
          <v-btn icon class="close-btn" @click.stop="fullscreenImage = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-img :src="fullscreenImage" contain class="fullscreen-img"></v-img>
        </div>
      </v-dialog>

    </div>
  </div>
</template>

<script>
import apiService from "@/services/ApiService";
import {getTranslation} from "@/services/stringTableService";

export default {
  name: 'EventsList',
  data() {
    return {
      events: [],
      language: "IT",
      fullscreenImage: null
    }
  },
  computed: {
    showFullScreenImage: {
      get() {
        return this.fullscreenImage != null;
      },
      set(val) {
        if (!val) this.fullscreenImage = null;
      }
    }
  },
  methods: {
    getTranslation(key) {
      return getTranslation(this.language, key);
    }
  },
  async beforeMount() {
    try {
      this.$store.dispatch('setLoading', true);
      const events = (await apiService.getEvents())
          .filter(ev => new Date(ev.expiration) >= new Date()) // Fixed expiration check logic
          .sort((a, b) => new Date(a.start) - new Date(b.start));
      this.events = events;
    } catch (e) {
      console.error(e);
    } finally {
      this.$store.dispatch('setLoading', false);
    }
  }
}
</script>

<style scoped lang="scss">
@import "../style/_variables.scss";

.events-list-wrapper {
  background-color: $background-color;
  min-height: 100dvh;
  padding: 10px 0px 40px;
  color: $text-color;  
  width: 100%;
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
  }
}

// Navigation (Matches ProductDetail)
.nav-header {
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: 2rem;
  
  .nav-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    cursor: pointer;
  }
  
  .back-btn {
    background: rgba(255,255,255,0.05);
    color: white;
    &:hover { background: $primary-gradient; }
  }
  
  .nav-title {
    font-family: $header-font;
    text-transform: uppercase;
    font-weight: bold;
    color: #ccc;
    font-size: 0.9rem;
    &:hover { color: white; }
  }

  .nav-logo-link {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
  }
  
  .header-logo {
     height: 60px;
     width: auto;
     transition: transform 0.3s;
     &:hover { transform: scale(1.1); }
  }
}

// Page Title
.page-title {
  text-align: center;
  font-family: $header-font;
  font-size: 2rem;
  text-transform: uppercase;
  color: white;
  margin-bottom: 0.5rem;
}
.title-underline {
  width: 60px;
  height: 4px;
  background: $primary-gradient;
  border-radius: 2px;
  margin: 0 auto 3rem;
}

// Events Grid
.events-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  padding-bottom: 2rem;
}

.event-card {
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.03); // Glassmorphism
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  animation: fadeUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) backwards;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.4);
    .hover-overlay { opacity: 1; }
    .event-img { transform: scale(1.05); }
  }

  .card-image-wrapper {
    position: relative;
    overflow: hidden;
    height: 500px; // Increased from 300px
    
    .event-img {
       height: 100%;
       width: 100%;
       transition: transform 0.5s ease;
    }
    
    .hover-overlay {
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s;
    }
  }
  
  .event-info {
    padding: 1.5rem;
    text-align: center;
    border-top: 1px solid rgba(255,255,255,0.05);
    
    .event-title {
      font-family: $header-font;
      font-size: 1.1rem;
      color: white;
      text-transform: capitalize;
    }
  }
}

// Empty State
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.1);
  max-width: 600px;
  margin: 0 auto;
  
  h3 { margin: 1rem 0; font-family: $header-font; color: $secondary-color; }
  p { color: #888; }
}

// Fullscreen
.fullscreen-wrapper {
  background: rgba(0,0,0,0.95);
  width: 100%; height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  .close-btn {
    position: absolute;
    top: 20px; right: 20px;
    color: black;
    background: rgba(255,255,255,0.8);
    z-index: 10;
    &:hover { background: white; }
  }
  
  .fullscreen-img {
    max-height: 90vh;
    max-width: 90vw;
  }
}

// Animations
.animate-fade-in { animation: fadeIn 0.8s ease forwards; }
.content-animate-up { 
  opacity: 0;
  animation: fadeUp 0.8s ease forwards 0.2s; 
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

// Mobile
@media (max-width: 600px) {
  .nav-header {
    justify-content: space-between;
    .nav-logo-link {
       position: static;
       transform: none;
    }
  }
  
  .events-grid {
    grid-template-columns: 1fr;
  }
}
</style>